import { TFunction } from "i18next";
import { BranchBasicInfo, ManagerInfo, BranchSettings } from "../../../../services/dto/request/branches-request-dto";
import { BranchStatus } from "../../../../constants/branch-status";
import { getDocumentLength } from "./document-lengths-by-country";
import { getDocumentErrorMessage, getDocumentPrefixErrorMessage } from "./error-messages-by-country";

export const PAYCLOUD_ACCOUNT_POSTAL_CODE = /^([0-9]{4}|[A-Z][0-9]{4}[A-Z]{3})$/;

export function isPaycloudAccountPostalCode(value: unknown): boolean {
  return typeof value === "string" && PAYCLOUD_ACCOUNT_POSTAL_CODE.test(value);
}

export function usesPaycloudAccountPostalCode(isEditMode?: boolean, status?: BranchStatus): boolean {
  return !isEditMode || status === BranchStatus.INCOMPLETE;
}

const localToday = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${today.getFullYear()}-${month}-${day}`;
};

const fantasyNameMin = 5;
const fantasyNameMax = 35;

export const validateStepOne = (
  branchBasicInfo: BranchBasicInfo,
  branchSettings: BranchSettings,
  setErrors: (errors: any) => void,
  t: TFunction<"translation", undefined>,
  status?: BranchStatus,
  existingFiles?: {
    afipFile: string | null;
    managerDniFront: string | null;
    managerDniBack: string | null;
    managerSelfie: string | null;
  },
  isEditMode?: boolean,
  isDominicanRepublic?: boolean,
  countryCode?: string
) => {
  
  // Obtener la longitud esperada del documento según el país
  const expectedDocumentLength = countryCode ? getDocumentLength(countryCode) : 11;
  
  let isValid = true;
  
  if (!branchBasicInfo.name?.trim()) {
    setErrors((prevErrors: any) => ({ ...prevErrors, name: t("branches.create.errors.name") }));
    isValid = false;
  } else if (branchBasicInfo.name.length < 5 || branchBasicInfo.name.length > 35) {
    setErrors((prevErrors: any) => ({ ...prevErrors, name: t("branches.create.errors.name-length") }));
    isValid = false;
  }

  if (isEditMode) {
    if (!branchBasicInfo.fantasyName?.trim()) {
      setErrors((prevErrors: any) => ({ ...prevErrors, fantasyName: t("branches.edit.errors.fantasy-name") }));
      isValid = false;
    } else if (branchBasicInfo.fantasyName.length < fantasyNameMin || branchBasicInfo.fantasyName.length > fantasyNameMax) {
      setErrors((prevErrors: any) => ({ ...prevErrors, fantasyName: t("branches.edit.errors.fantasy-name-length") }));
      isValid = false;
    }
  }

  if (!branchBasicInfo.address?.trim()) {
    setErrors((prevErrors: any) => ({ ...prevErrors, address: t("branches.create.errors.address") }));
    isValid = false;
  } else if (branchBasicInfo.address.length < 5) {
    setErrors((prevErrors: any) => ({ ...prevErrors, address: t("branches.create.errors.address-length") }));
    isValid = false;
  }

  if (!branchBasicInfo.phone?.trim()) {
    setErrors((prevErrors: any) => ({ ...prevErrors, phone: t("branches.create.errors.phone-required") }));
    isValid = false;
  } else if (branchBasicInfo.phone.length < 7 || branchBasicInfo.phone.length > 20) {
    setErrors((prevErrors: any) => ({ ...prevErrors, phone: t("branches.create.errors.phone-size") }));
    isValid = false;
  }

  if(branchSettings.createCBU && !isDominicanRepublic) {
    if (!branchSettings.identityNumber || branchSettings.identityNumber.length !== expectedDocumentLength) {
      // Mensaje de error según el país (normalizado)
      const errorMessage = getDocumentErrorMessage(countryCode || '', t, branchSettings.identityType);
      setErrors((prevErrors: any) => ({ ...prevErrors, identityNumber: errorMessage}));
      isValid = false;
    } else {
      // Validar prefijos para Argentina
      const prefix = branchSettings.identityNumber.substring(0, 2);
      if (branchSettings.identityType === "CUIL") {
        if (!["20", "23", "24", "27"].includes(prefix)) {
          const prefixErrorMessage = getDocumentPrefixErrorMessage(countryCode || '', t, branchSettings.identityType);
          if (prefixErrorMessage) {
            setErrors((prevErrors: any) => ({ ...prevErrors, identityNumber: prefixErrorMessage}));
            isValid = false;
          }
        }
      } else if (branchSettings.identityType === "CUIT") {
        if (!["30", "33", "34"].includes(prefix)) {
          const prefixErrorMessage = getDocumentPrefixErrorMessage(countryCode || '', t, branchSettings.identityType);
          if (prefixErrorMessage) {
            setErrors((prevErrors: any) => ({ ...prevErrors, identityNumber: prefixErrorMessage}));
            isValid = false;
          }
        }
      }
    }

    // Validaciones de provincia, ciudad, etc. solo para Argentina
    if (!branchBasicInfo.branchProvinceId || branchBasicInfo.branchProvinceId === 0) {
      setErrors((prevErrors: any) => ({...prevErrors, branchProvinceId: t("branches.create.errors.branch-province-id")}));
      isValid = false;
    }

    if (!branchBasicInfo.branchCityId || branchBasicInfo.branchCityId === 0) {
      setErrors((prevErrors: any) => ({...prevErrors, branchCityId: t("branches.create.errors.branch-city-id")}));
      isValid = false;
    }

    if (branchBasicInfo.floor.length > 2) {
      setErrors((prevErrors: any) => ({...prevErrors, floor: t("branches.create.errors.floor")}));
      isValid = false;
    }

    const postalCode = branchBasicInfo.postalCode;
    const postalCodeIsValid = usesPaycloudAccountPostalCode(isEditMode, status)
      ? isPaycloudAccountPostalCode(postalCode)
      : !!postalCode && (postalCode.length === 4 || postalCode.length === 8);
    if (!postalCodeIsValid) {
      setErrors((prevErrors: any) => ({...prevErrors, postalCode: t("branches.create.errors.postal-code")}));
      isValid = false;
    }

    const requireKycFields = !isEditMode || status === BranchStatus.INCOMPLETE;
    if (requireKycFields) {
      if (!branchBasicInfo.birthDate) {
        setErrors((prevErrors: any) => ({ ...prevErrors, birthDate: t("branches.create.errors.birth-date") }));
        isValid = false;
      } else if (branchBasicInfo.birthDate > localToday()) {
        setErrors((prevErrors: any) => ({ ...prevErrors, birthDate: t("branches.create.errors.birth-date-future") }));
        isValid = false;
      }

      const occupation = branchBasicInfo.occupation?.trim() ?? "";
      if (occupation.length < 4 || occupation.length > 40) {
        setErrors((prevErrors: any) => ({ ...prevErrors, occupation: t("branches.create.errors.occupation") }));
        isValid = false;
      }

      if (!branchBasicInfo.ivaCondition) {
        setErrors((prevErrors: any) => ({ ...prevErrors, ivaCondition: t("branches.create.errors.iva-condition") }));
        isValid = false;
      }

      if (!branchBasicInfo.gender) {
        setErrors((prevErrors: any) => ({ ...prevErrors, gender: t("branches.create.errors.gender") }));
        isValid = false;
      }

      if (!branchBasicInfo.civilStatus) {
        setErrors((prevErrors: any) => ({ ...prevErrors, civilStatus: t("branches.create.errors.civil-status") }));
        isValid = false;
      }

      if (branchBasicInfo.isPEP) {
        const pepPosition = branchBasicInfo.pepPosition?.trim() ?? "";
        if (pepPosition.length < 4 || pepPosition.length > 40) {
          setErrors((prevErrors: any) => ({ ...prevErrors, pepPosition: t("branches.create.errors.pep-position") }));
          isValid = false;
        }
      }
    }

    if (!status || status !== BranchStatus.APPROVED) {
      // Verificar si hay archivo AFIP (nuevo o existente)
      const hasAfipFile = branchBasicInfo.afipFile || existingFiles?.afipFile;
      if (!hasAfipFile) {
        setErrors((prevErrors: any) => ({...prevErrors, afipFile: t("branches.create.errors.afip-file")}));
        isValid = false;
      }
    }

    if (branchBasicInfo.department.length > 3) {
      setErrors((prevErrors: any) => ({...prevErrors, department: t("branches.create.errors.department")}));
      isValid = false;
    }

    // Validaciones de dirección de actividad (solo para Argentina)
    if (!branchBasicInfo.sameAddress) {
      if (!branchBasicInfo.activityAddress?.trim()) {
        setErrors((prevErrors: any) => ({...prevErrors, activityAddress: t("branches.create.errors.activity-address")}));
        isValid = false;
      } else if (branchBasicInfo.activityAddress.length < 5) {
        setErrors((prevErrors: any) => ({...prevErrors, activityAddress: t("branches.create.errors.activity-address-length")}));
        isValid = false;
      }

      if (
        branchBasicInfo.activityFloor &&
        branchBasicInfo.activityFloor.length > 2
      ) {
        setErrors((prevErrors: any) => ({...prevErrors, activityFloor: t("branches.create.errors.activity-floor")}));
        isValid = false;
      }

      if (
        !branchBasicInfo.activityPostalCode ||
        (branchBasicInfo.activityPostalCode.length !== 4 &&
          branchBasicInfo.activityPostalCode.length !== 8)
      ) {
        setErrors((prevErrors: any) => ({...prevErrors, activityPostalCode: t("branches.create.errors.activity-postal-code")}));
        isValid = false;
      }

      if (
        branchBasicInfo.activityDepartment &&
        branchBasicInfo.activityDepartment.length > 3
      ) {
        setErrors((prevErrors: any) => ({...prevErrors, activityDepartment: t("branches.create.errors.activity-department")}));
        isValid = false;
      }
    }
  } // Cierre del if createCBU && !isDominicanRepublic
  
  return isValid;
};

export const validateStepTwo = (
  managerInfo: ManagerInfo,
  setErrors: (errors: any) => void,
  t: TFunction<"translation", undefined>,
  existingFiles?: {
    afipFile: string | null;
    managerDniFront: string | null;
    managerDniBack: string | null;
    managerSelfie: string | null;
  }
) => {
  let isValid = true;

  if(!managerInfo.managerName?.trim()) {
    setErrors((prevErrors: any) => ({ ...prevErrors, managerName: t("branches.create.errors.managerName") }));
    isValid = false;
  } else if (managerInfo.managerName.length < 3 || managerInfo.managerName.length > 19) {
    setErrors((prevErrors: any) => ({ ...prevErrors, managerName: t("branches.create.errors.managerName-length") })); 
    isValid = false;  
  }

  if(!managerInfo.managerLastName?.trim()) {
    setErrors((prevErrors: any) => ({ ...prevErrors, managerLastName: t("branches.create.errors.managerLastName") }));
    isValid = false;
  } else if (managerInfo.managerLastName.length < 3 || managerInfo.managerLastName.length > 19) {
    setErrors((prevErrors: any) => ({ ...prevErrors, managerLastName: t("branches.create.errors.managerLastName-length") }));
    isValid = false;
  }

  if(!managerInfo.managerPhone || managerInfo.managerPhone.length !== 10 || !/^\d{10}$/.test(managerInfo.managerPhone)) {
    setErrors((prevErrors: any) => ({ ...prevErrors, managerPhone: t("branches.create.errors.managerPhone") }));
    isValid = false;
  } 

  if(!managerInfo.managerAddress || managerInfo.managerAddress.length < 5) {
    setErrors((prevErrors: any) => ({ ...prevErrors, managerAddress: t("branches.create.errors.managerAddress") }));
    isValid = false;
  } else if (managerInfo.managerAddress.length > 50) {
    setErrors((prevErrors: any) => ({ ...prevErrors, managerAddress: t("branches.create.errors.managerAddress-max") }));
    isValid = false;
  } 

  if(!managerInfo.managerProvinceId || managerInfo.managerProvinceId === 0) {
    setErrors((prevErrors: any) => ({
      ...prevErrors,
      managerProvinceId: t("branches.create.errors.branch-province-id"),
    }));
    isValid = false;
  }

  if (!managerInfo.managerDocumentNumber || managerInfo.managerDocumentNumber.length !== 11) {
    setErrors((prevErrors: any) => ({ ...prevErrors, managerDocumentNumber: t("branches.create.errors.cuit-cuil")}));
    isValid = false;
  } else {
    const prefix = managerInfo.managerDocumentNumber.substring(0, 2);
    if (managerInfo.managerDocumentType === "CUIL") {
      if (!["20", "23", "24", "27"].includes(prefix)) {
          setErrors((prevErrors: any) => ({ ...prevErrors, managerDocumentNumber: t("branches.create.errors.cuit-cuil-physical")}));
          isValid = false;
        }
      }
    }

  if(!managerInfo.managerCityId || managerInfo.managerCityId === 0) {
    setErrors((prevErrors: any) => ({
      ...prevErrors,
      managerCityId: t("branches.create.errors.branch-city-id"),
    }));
    isValid = false;
  }

  // Verificar documentos del manager (nuevos o existentes)
  const hasDniFront = managerInfo.managerDniFront || existingFiles?.managerDniFront;
  const hasDniBack = managerInfo.managerDniBack || existingFiles?.managerDniBack;
  const hasSelfie = managerInfo.managerSelfie || existingFiles?.managerSelfie;

  if (!hasDniFront || !hasDniBack || !hasSelfie) {
    const missingDocs: string[] = [];
    if (!hasDniFront) missingDocs.push(t("branches.create.errors.managerDniFront"));
    if (!hasDniBack) missingDocs.push(t("branches.create.errors.managerDniBack"));
    if (!hasSelfie) missingDocs.push(t("branches.create.errors.managerSelfie"));

    setErrors((prevErrors: any) => ({
      ...prevErrors,
      dniDocuments: `${t("branches.create.errors.dniDocuments")}: ${missingDocs.join(", ")}`,
    }));
    isValid = false;
  } 
  return isValid;
};
  
  
  
  
  
